import { BehaviorSubject, Observable, of, throwError as observableThrowError } from 'rxjs';
import { catchError, first, map, take } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { select, Store } from '@ngrx/store';
import { filter, find, sortBy, uniq } from 'lodash';
import moment from 'moment';
import { firstValueFrom } from 'rxjs/internal/firstValueFrom';
import { UserService } from './user.service';
import {
  Crop,
  Field,
  FieldActivity,
  FieldActivityGrouped,
  FieldActivityItem,
  User,
} from '../_models';
import { CropMetricsService } from './cropMetrics.service';
import { FieldActivityTypes } from '../../../assets/data/enum/fieldActivityTypes';
import { UnitMeasurement } from '../../../assets/data/enum/unit_measurement.enum';
import { AppState } from '../_reducers';
import { environment } from '../../../environments/environment';
import { OperationStateService } from './operation-state.service';
import { selectAllFieldActivities } from '../_selectors/fieldActivity.selectors';
import { PaginatedResponse } from '../_models/paginatedResponse';
import { XYearService } from './xYear.service';
import { TagService } from './tag.service';
import { FieldActivitiesCustomType } from '../_models/fieldActivityCustomType';

@Injectable()
export class FieldActivityService {
  private apiBaseUrl = environment.apiBaseUrl + 'api/private/v1/';
  private apiUrl = environment.apiBaseUrl;
  private activityServiceUrl = environment.activityServiceUrl + 'api/private/v2/cache/';
  private unitMeasurement = UnitMeasurement;
  fieldActivityList: Observable<FieldActivity[]>;
  fieldActivityGroupedList$: Observable<FieldActivityGrouped[]>;
  selectActivitiesTab$: BehaviorSubject<string> = new BehaviorSubject<string>('grouped');
  resultList$: BehaviorSubject<Array<FieldActivity>> = new BehaviorSubject<Array<FieldActivity>>(
    []
  );
  originalFieldActivityList$: BehaviorSubject<Array<FieldActivity>> = new BehaviorSubject<
    Array<FieldActivity>
  >([]);
  showLoaderFieldActivityGrouped$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  showLoaderLastFieldActivityGrouped$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false
  );
  currentUser: User = new User();
  allFieldActivity$: BehaviorSubject<Array<FieldActivity>> = new BehaviorSubject<
    Array<FieldActivity>
  >(null);
  lastFieldActivity$: BehaviorSubject<Array<FieldActivity>> = new BehaviorSubject<
    Array<FieldActivity>
  >([]);
  allFieldActivityGrouped$: BehaviorSubject<Array<FieldActivityGrouped>> = new BehaviorSubject<
    Array<FieldActivityGrouped>
  >([]);
  fieldActivitiesCustomType$: BehaviorSubject<FieldActivitiesCustomType[]> = new BehaviorSubject<
    FieldActivitiesCustomType[]
  >([]);
  countCallFa = 0;
  countCallFaGroped = 0;
  cropResiduesDestinations = [
    'removed_from_field_for_use',
    'removed_from_field_for_sale',
    'left_on_field',
    'burned_in_field',
  ];

  constructor(
    private http: HttpClient,
    private jwtService: OperationStateService,
    private store: Store<AppState>,
    private cropMetricsService: CropMetricsService,
    private userService: UserService,
    private xYearService: XYearService,
    private tagService: TagService
  ) {
    if (environment.local) {
      // pointing to the cache disabled to point to the monolith directly with api/private/v1
      this.activityServiceUrl = environment.apiBaseUrl + 'api/private/v1/';
    }
    this.fieldActivityList = this.store.pipe(select(selectAllFieldActivities));
    this.fieldActivityList.subscribe((res) => {
      this.originalFieldActivityList$.next(res);
    });

    this.userService.getUserMeFromLocalStore().subscribe((u: User) => {
      this.currentUser = u;
    });
  }

  getFromLocalStore(): Observable<FieldActivity[]> {
    return this.fieldActivityList;
  }

  getFA() {
    return this.originalFieldActivityList$;
  }

  setActivitiesTab(tab) {
    this.selectActivitiesTab$.next(tab);
  }

  getActivitiesTab() {
    return this.selectActivitiesTab$.asObservable();
  }

  getFieldActivities(
    selectedYear: number,
    forceBaseUrl = null,
    tags?: string
  ): Observable<FieldActivity[]> {
    const year = selectedYear ?? this.xYearService.getYear();
    const tagQuery = this.tagService.getTagQuery();
    let fallBackActivityCheck = true;
    if (forceBaseUrl && forceBaseUrl != null && forceBaseUrl != '') {
      fallBackActivityCheck = false;
      this.activityServiceUrl = forceBaseUrl;
    }
    let onlyPersonalActivity: boolean = false;
    let onlyPersonalActivityEmployee: boolean = false;
    if (this.currentUser && this.currentUser.userSettings) {
      if (this.currentUser.userSettings.activityPersonalInfo !== undefined) {
        onlyPersonalActivity = this.currentUser.userSettings.activityPersonalInfo;
      }
      if (this.currentUser.userSettings.activityPersonalInfoEmployee !== undefined) {
        onlyPersonalActivityEmployee = this.currentUser.userSettings.activityPersonalInfoEmployee;
      }
    }
    if (onlyPersonalActivityEmployee) {
      onlyPersonalActivity = onlyPersonalActivityEmployee;
    }
    const excludesList = ['machineActivity', 'machineActivities', 'equipmentActivities'];
    let excludesAppend = '';
    excludesList.forEach((item) => {
      excludesAppend += '&excludes=' + item;
    });

    return this.http
      .get<
        FieldActivity[]
      >(this.activityServiceUrl + 'fieldActivities?onlyPersonalActivity=' + onlyPersonalActivity + excludesAppend + '&cropYear=' + year + tagQuery)
      .pipe(
        map((response) => {
          if (response) {
            if (response) {
              this.showLoaderFieldActivityGrouped$.next(false);
              this.allFieldActivity$.next(response);
              return response;
            }
          }
        }),
        catchError((error: HttpErrorResponse) => {
          if (
            (error.status === 403 || error.status === 400 || error.status === 504) &&
            fallBackActivityCheck &&
            this.countCallFa === 0
          ) {
            this.jwtService.enabledMessageAlertAPI$.next(false);
            this.countCallFa = this.countCallFa + 1;
            return this.getFieldActivities(year, this.apiBaseUrl, tags);
          } else {
            // Altrimenti, gestisci l'errore come di consueto
            return observableThrowError(error);
          }
        })
      );
  }

  getFieldActivitiesWithPagination(
    selectedYear: number = null,
    all: boolean = null,
    max: number = null,
    offset: number = null,
    sort: string = null,
    order: string = null
  ): Observable<FieldActivity[]> {
    this.showLoaderLastFieldActivityGrouped$.next(true);

    let onlyPersonalActivity = 'false';
    if (this.currentUser && this.currentUser.userSettings) {
      if (this.currentUser.userSettings.activityPersonalInfo !== undefined) {
        onlyPersonalActivity = this.currentUser.userSettings.activityPersonalInfo.toString();
      }
    }

    let params = new HttpParams()
      .set('onlyPersonalActivity', onlyPersonalActivity)
      .set('cropYear', selectedYear?.toString() || '')
      .append('excludes', 'machineActivity')
      .append('excludes', 'machineActivities')
      .append('excludes', 'equipmentActivities');

    if (all != null) {
      params = params.set('all', all.toString());
    }
    if (max != null) {
      params = params.set('max', max.toString());
    }
    if (offset != null) {
      params = params.set('offset', offset.toString());
    }
    if (sort != null) {
      params = params.set('sort', sort);
    }
    if (order != null) {
      params = params.set('order', order);
    }

    return this.http
      .get<FieldActivity[]>(this.activityServiceUrl + 'fieldActivities', { params })
      .pipe(
        map((response) => {
          this.showLoaderLastFieldActivityGrouped$.next(false);
          this.lastFieldActivity$.next(response);
          return response;
        })
      );
  }

  getFieldActivitiesForTags(
    selectedYear: number | null = null,
    all: boolean | null = null,
    max: number | null = null,
    offset: number | null = null,
    sort: string | null = null,
    order: string | null = null,
    queryTag: string | null = null
  ): Observable<any> {
    this.showLoaderLastFieldActivityGrouped$.next(true);

    let params = new HttpParams();
    if (all !== null) {
      params = params.set('all', all.toString());
    }
    if (max !== null) {
      params = params.set('max', max.toString());
    }
    if (offset !== null) {
      params = params.set('offset', offset.toString());
    }
    if (sort !== null) {
      params = params.set('sort', sort);
    }
    if (order !== null) {
      params = params.set('order', order);
    }

    let onlyPersonalActivity = false;
    if (this.currentUser?.userSettings?.activityPersonalInfo !== undefined) {
      onlyPersonalActivity = this.currentUser.userSettings.activityPersonalInfo;
    }

    const excludesList = ['machineActivity', 'machineActivities', 'equipmentActivities'];
    const excludesAppend = excludesList.map((item) => `&excludes=${item}`).join('');

    return this.http
      .get<any>(
        `${this.activityServiceUrl}fieldActivities?onlyPersonalActivity=${onlyPersonalActivity}${excludesAppend}&cropYear=${selectedYear}${queryTag ?? ''}`,
        { params }
      )
      .pipe(
        map((response) => {
          this.showLoaderLastFieldActivityGrouped$.next(false);
          this.lastFieldActivity$.next(response);
          return response;
        }),
        catchError((error) => {
          console.error('Error occurred while fetching field activities:', error);
          this.showLoaderLastFieldActivityGrouped$.next(false);
          throw new Error('Failed to fetch field activities');
        })
      );
  }

  getFieldActivity(id: number): Observable<FieldActivity> {
    return this.http
      .get<FieldActivity>(this.apiBaseUrl + 'fieldActivities/' + id)
      .pipe(map((response) => response));
  }

  saveFieldActivity(fieldActivityItem): Observable<FieldActivity> {
    const fieldActivity = { ...fieldActivityItem };
    delete fieldActivity.lastUpdated;
    delete fieldActivity.dateCreated;
    delete fieldActivity.cropObj;

    if (fieldActivity.mixes) {
      const arrmixes: Array<any> = [];
      fieldActivity.mixes.forEach((element) => {
        if (element.product) {
          const idproduct: any = {
            id: element.product.id,
          };
          element.product = idproduct;
        }
        arrmixes.push(element);
      });
      fieldActivity.mixes = arrmixes;
    }
    if (fieldActivity.equipments) {
      const arrequipments: Array<any> = [];
      fieldActivity.equipments.forEach((element) => {
        const idequipment: any = {
          id: element.id,
        };
        arrequipments.push(idequipment);
      });
      fieldActivity.equipments = arrequipments;
    }
    if (fieldActivity.users) {
      const arrusers: Array<any> = [];
      fieldActivity.users.forEach((element) => {
        const iduser: any = {
          id: element.id,
        };
        arrusers.push(iduser);
      });
      fieldActivity.users = arrusers;
    }

    if (fieldActivity.field) {
      const idfield: any = {
        id: fieldActivity.field.id,
      };
      fieldActivity.field = idfield;
    }
    delete fieldActivity.verificationDate;
    delete fieldActivity.machineActivity;
    delete fieldActivity.machineActivities;
    const companySelected = JSON.parse(localStorage.getItem('companySelected'));
    fieldActivity.companyId = companySelected ? companySelected.id : null;

    if (fieldActivity.id) {
      return this.http
        .put<FieldActivity>(this.apiBaseUrl + 'fieldActivities/' + fieldActivity.id, fieldActivity)
        .pipe(
          map((response) => {
            if (response) {
              this.cropMetricsService.getCropMetrics().subscribe();
              return response;
            }
          })
        );
    } else {
      return this.http
        .post<FieldActivity>(this.apiBaseUrl + 'fieldActivities/', fieldActivity)
        .pipe(
          map((response) => {
            if (response) {
              this.cropMetricsService.getCropMetrics().subscribe();
              return response;
            }
          })
        );
    }
  }

  deleteFieldActivity(id: number): Observable<FieldActivity> {
    return this.http.delete(this.apiBaseUrl + 'fieldActivities/' + id).pipe(
      map((response) => {
        if (response) {
          this.cropMetricsService.getCropMetrics().subscribe();
          return { id } as FieldActivity;
        }
      })
    );
  }

  getLocalFieldActivityById = (id): FieldActivity => {
    const list = this.originalFieldActivityList$.value;
    const item = list && list.find((i) => i.id === id);
    return item ? JSON.parse(JSON.stringify(item)) : null;
  };

  async getLocalFieldActivityByUUID(uuid: string): Promise<Array<FieldActivity>> {
    if (!uuid) {
      return [];
    }

    const fas = await firstValueFrom(
      this.fieldActivityList.pipe(first((list: FieldActivity[]) => list && list.length > 0))
    );

    return fas.filter((f) => f.uuid === uuid);
  }

  getLocalFieldActivityDone(): Array<FieldActivityGrouped> {
    let fasList: Array<FieldActivityGrouped> = [];

    this.fieldActivityGroupedList$.pipe(take(1)).subscribe((fas) => {
      fasList = fas.filter((f) => f.done === true || f.done === null || f.done === undefined);
    });

    return fasList;
  }

  getFieldActivityGroupedByStatus(fieldActivityGrouped: FieldActivityGrouped[]): {
    done: FieldActivityGrouped[];
    pending: FieldActivityGrouped[];
  } {
    return {
      done:
        fieldActivityGrouped?.filter(
          (f) => f.done === true || f.done === null || f.done === undefined
        ) || [],
      pending: fieldActivityGrouped?.filter((f) => f.done === false) || [],
    };
  }

  getLocal2FieldActivityById = (id: number): FieldActivity => {
    let item: FieldActivity;
    this.originalFieldActivityList$
      .pipe(first((list: FieldActivity[]) => list && list.length > 0))
      .subscribe((list: FieldActivity[]) => {
        item = list.find((i) => i.id === id);
      });
    return item;
  };

  setFilteredFaByIdByObservablelist(faIds: Array<number>) {
    const tmpAct = [];
    this.resultList$.next(tmpAct);
    faIds.forEach((id) => {
      if (this.getLocal2FieldActivityById(id)) {
        tmpAct.push(this.getLocalFieldActivityById(id));
      }
      this.resultList$.next(tmpAct);
    });
  }

  getFilteredFaByIdByObservablelist() {
    return this.resultList$.asObservable();
  }

  getUnitMeasurementConfiguration() {
    return [this.unitMeasurement.KG_HA, this.unitMeasurement.L_HA, this.unitMeasurement.UNIT];
  }

  getUnitMeasurementConfigurationSeeding() {
    return [this.unitMeasurement.KG_HA, this.unitMeasurement.UNIT];
  }

  getUnitMeasurementConfigurationIrrigating() {
    return [
      this.unitMeasurement.L_S,
      this.unitMeasurement.MQ_H,
      this.unitMeasurement.L_MIN,
      this.unitMeasurement.L_H,
      this.unitMeasurement.MM,
    ];
  }

  getUnitMeasurementConfigurationFieldSubmersion() {
    return [this.unitMeasurement.L, this.unitMeasurement.MC];
  }

  getUnitMeasurementConfigurationDeposit(): string[] {
    return [this.unitMeasurement.KG, this.unitMeasurement.L, this.unitMeasurement.BU];
  }

  checkIfActivitiesExistInField(field: Field): boolean {
    const localFieldActivityDone = this.getLocalFieldActivityDone();

    if (localFieldActivityDone && localFieldActivityDone.length > 0) {
      return localFieldActivityDone.some((item) => item.fieldIds.includes(field.id));
    }

    return false;
  }

  retrievLastDoneActivityFromField(field: Field, year: any): FieldActivity {
    const filteredLocalFieldActivityDone = this.getLocalFieldActivityDone().filter((item) =>
      item.fieldIds.includes(field.id)
    );

    if (filteredLocalFieldActivityDone && filteredLocalFieldActivityDone.length > 0) {
      // If there is at least one activity with the searched ID, return the last one based on the creation date.
      const lastFieldActivity = filteredLocalFieldActivityDone.reduce((prev, current) =>
        new Date(prev.activityDate + '') > new Date(current.activityDate + '') ? prev : current
      );
      return this.getLocalFieldActivityById(lastFieldActivity.ids[0]);
    }

    return null;
  }

  getExportType = (): Array<any> => {
    const result: Array<any> = [
      {
        type: 'FieldActivityList',
        subtype: 'FieldActivityList',
        formats: [
          {
            type: 'csv',
            enabled: true,
            compression: false,
            col: '6',
          },
          {
            type: 'xls',
            enabled: true,
            compression: false,
            col: '6',
          },
        ],
      },
      {
        type: 'FieldActivities',
        subtype: 'WorkPlan',
        formats: [
          {
            type: 'xlsx',
            enabled: true,
            compression: false,
            col: '12',
          },
        ],
      },
      {
        type: 'FieldActivities',
        subtype: 'CultivationPlan',
        formats: [
          {
            type: 'csv',
            enabled: false,
            compression: true,
            col: '4',
          },
          {
            type: 'xls',
            enabled: true,
            compression: false,
            col: '4',
          },
          {
            type: 'pdf',
            enabled: true,
            compression: false,
            col: '4',
          },
        ],
      },
      {
        type: 'FieldActivities',
        subtype: 'Treatments',
        formats: [
          {
            type: 'csv',
            enabled: false,
            compression: true,
            col: '4',
          },
          {
            type: 'xls',
            enabled: true,
            compression: false,
            col: '4',
          },
          {
            type: 'pdf',
            enabled: true,
            compression: false,
            col: '4',
          },
        ],
      },
      {
        type: 'FieldActivities',
        subtype: 'PlotsAndParcels',
        formats: [
          {
            type: 'csv',
            enabled: false,
            compression: true,
            col: '4',
          },
          {
            type: 'xls',
            enabled: true,
            compression: false,
            col: '4',
          },
          {
            type: 'pdf',
            enabled: true,
            compression: false,
            col: '4',
          },
        ],
      },
    ];
    return result;
  };

  getExportTypeEquipment = (): Array<any> => {
    const result: Array<any> = [
      {
        type: 'EquipmentActivities',
        subtype: 'CompleteEquipmentActivityListToLocalExport',
        formats: [
          {
            type: 'csv',
            enabled: true,
            compression: false,
            col: '6',
          },
          {
            type: 'xls',
            enabled: true,
            compression: false,
            col: '6',
          },
        ],
      },
    ];
    return result;
  };

  checkIfCropExistInFieldActivity(idCrop: number): Observable<any> {
    if (idCrop) {
      return this.fieldActivityList.pipe(
        map((fieldActivityList: FieldActivity[]) =>
          fieldActivityList.filter((f) => f.crop && f.crop.id === idCrop)
        )
      );
    }
  }

  checkFieldActivitiesByCropId(crop: Crop): Observable<any> {
    let simbol = '';
    let queryString = '';

    if (crop && crop.uuid) {
      simbol = queryString.indexOf('?') !== -1 ? '&' : '?';
      queryString = queryString + simbol + 'cropUuid=' + crop.uuid;
    } else {
      if (crop && crop.id) {
        simbol = queryString.indexOf('?') !== -1 ? '&' : '?';
        queryString = queryString + simbol + 'cropId=' + crop.id;
      }
    }

    return this.http
      .get<any>(this.apiBaseUrl + 'fieldActivities/' + queryString)
      .pipe(map((response) => response));
  }

  checkIfCropListExistInFieldActivity(cropList: Array<any>): Observable<any> {
    if (cropList && cropList.length > 0) {
      return this.fieldActivityList.pipe(
        map((fieldActivityList: FieldActivity[]) =>
          fieldActivityList.filter((f) => f.crop && find(cropList, { id: f.crop.id }))
        )
      );
    }
  }

  getAllFieldActivityTypes = (): Array<string> => {
    const keys = sortBy(this.getAllKeys(FieldActivityTypes));
    const keysWithoutLevels = keys.map((key) => (key.includes('.') ? key.split('.')[1] : key));

    return keysWithoutLevels;
  };

  getAllKeys(obj: any): string[] {
    let keys: string[] = [];

    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        keys.push(key);
        if (typeof obj[key] === 'object' && Object.keys(obj[key]).length > 0) {
          const subKeys = this.getAllKeys(obj[key]);
          keys = keys.concat(subKeys.map((subKey) => `${key}.${subKey}`));
        }
      }
    }

    return keys;
  }

  getMetoBlueStatus = () => {
    return [
      'clear',
      'cloudy',
      'changeable',
      'very_cloudy',
      'overcast',
      'foggy',
      'overcast_with_rain',
      'changeable_with_rain_showers',
      'thunderstorm',
      'overcast_with_snow',
      'changeable_with_snow_showers',
      'rainfall_mixed_with_snow',
    ];
  };

  getFieldActivitiesOPModule(farmsAppend, selectedYear): Observable<FieldActivity[]> {
    const excludesList = ['machineActivity', 'machineActivities', 'equipmentActivities'];

    const searchParams = new URLSearchParams(farmsAppend);

    excludesList.forEach((e) => {
      searchParams.append('excludes', e);
    });

    searchParams.set('cropYear', selectedYear);

    return this.http
      .get<FieldActivity[]>(this.activityServiceUrl + 'fieldActivities?' + searchParams.toString())
      .pipe(map((response) => response));
  }

  getFieldActivityGrouped(
    farmsAppend?,
    selectedYear?: number,
    forceBaseUrl = null
  ): Observable<FieldActivityGrouped[]> {
    const farms = farmsAppend ?? '';
    const year = selectedYear ?? this.xYearService.getYear();
    const tagQuery = this.tagService.getTagQuery();
    let fallBackActivityCheck = true;
    if (forceBaseUrl && forceBaseUrl != null && forceBaseUrl != '') {
      fallBackActivityCheck = false;
      this.activityServiceUrl = forceBaseUrl;
    }
    this.showLoaderFieldActivityGrouped$.next(true);
    let onlyPersonalActivity: boolean = false;
    let onlyPersonalActivityEmployee: boolean = false;
    if (this.currentUser && this.currentUser.userSettings) {
      if (this.currentUser.userSettings.activityPersonalInfo !== undefined) {
        onlyPersonalActivity = this.currentUser.userSettings.activityPersonalInfo;
      }
      if (this.currentUser.userSettings.activityPersonalInfoEmployee !== undefined) {
        onlyPersonalActivityEmployee = this.currentUser.userSettings.activityPersonalInfoEmployee;
      }
    }
    const excludesList = [
      'activities',
      'machineActivity',
      'machineActivities',
      'equipmentActivities',
    ];
    let excludesAppend = '';
    excludesList.forEach((item) => {
      excludesAppend += '&excludes=' + item;
    });
    if (onlyPersonalActivityEmployee) {
      onlyPersonalActivity = onlyPersonalActivityEmployee;
    }

    return this.http
      .get<
        FieldActivityGrouped[]
      >(this.activityServiceUrl + 'fieldActivityGrouped?' + farms + excludesAppend + '&cropYear=' + year + '&onlyPersonalActivity=' + onlyPersonalActivity + tagQuery)
      .pipe(
        map((response) => {
          if (response) {
            if (response) {
              this.showLoaderFieldActivityGrouped$.next(false);
              this.setFieldActivityGroupedBehaviorSubject(response);
              return response;
            }
          }
        }),
        catchError((error: HttpErrorResponse) => {
          if (
            (error.status === 403 || error.status === 400 || error.status === 504) &&
            fallBackActivityCheck &&
            this.countCallFaGroped === 0
          ) {
            this.jwtService.enabledMessageAlertAPI$.next(false);
            this.showLoaderFieldActivityGrouped$.next(true);
            this.countCallFaGroped = this.countCallFaGroped + 1;

            // Se l'errore è di tipo CORS o 403, passa alla seconda API
            return this.getFieldActivityGrouped(farmsAppend, year, this.apiBaseUrl);
          } else {
            this.jwtService.enabledMessageAlertAPI$.next(true);
            // Altrimenti, gestisci l'errore come di consueto
            return observableThrowError(error);
          }
        })
      );
  }

  getFieldActivityGroupedByUUID(uuid): Observable<FieldActivityGrouped> {
    return this.http
      .get<FieldActivityGrouped>(this.apiBaseUrl + 'fieldActivityGrouped/' + uuid)
      .pipe(
        map((response) => {
          if (response) {
            return response;
          }
        }),
        catchError((error: HttpErrorResponse) => {
          return observableThrowError(error);
        })
      );
  }
  getFieldActivityGroupedBySingleActivityId(id): Observable<FieldActivityGrouped> {
    return this.http.get<any>(this.apiBaseUrl + 'fieldActivities/' + id).pipe(
      map((response) => {
        if (response) {
          const FAGrouped: FieldActivityGrouped = new FieldActivityGrouped();
          const FA = response;
          FAGrouped.activities = [];
          FAGrouped.equipmentActivities = FA.machineActivities;
          FAGrouped.uuid = FA.uuid;
          FAGrouped.activities.push(FA);
          return FAGrouped;
        }
      }),
      catchError((error: HttpErrorResponse) => {
        return observableThrowError(error);
      })
    );
  }

  newFilterForFieldActivities(
    fieldActivities: FieldActivity[],
    startDate: number,
    endDate: number,
    userIds: Array<number>,
    fieldIds?: number[]
  ) {
    let dateMatch = true;

    let filteredFieldActivities = [];
    if (userIds && userIds.length > 0) {
      fieldActivities.forEach((singleAct) => {
        singleAct.users?.forEach((singleUser) => {
          if (userIds.includes(singleUser.id)) {
            filteredFieldActivities.push(singleAct);
          }
        });
      });
    } else {
      filteredFieldActivities = fieldActivities;
    }

    filteredFieldActivities = filter(filteredFieldActivities, (item) => {
      if (startDate && startDate != 0 && endDate && endDate != 0) {
        dateMatch =
          moment(item.activityDate).startOf('day').valueOf() >=
            moment(startDate).startOf('day').valueOf() &&
          moment(item.activityDate).startOf('day').valueOf() <=
            moment(endDate).endOf('day').valueOf();
      }
      return dateMatch;
    });

    // Filter out fieldActivities that are not in the fieldIds array
    filteredFieldActivities = filter(filteredFieldActivities, (item) => {
      if (fieldIds && fieldIds.length > 0) {
        return fieldIds.includes(item.field.id);
      }
      return true;
    });

    const uniqGhostFiltered = uniq(filteredFieldActivities);
    return uniqGhostFiltered;
  }

  // tslint:disable-next-line: max-line-length
  filterForFieldAcitivitiesGrouped(
    activitiesGrouped: Array<FieldActivityGrouped>,
    startDate: number,
    endDate: number,
    userIds: Array<number>
  ) {
    let ghostFilterd = [];
    let dateMatch = true;
    activitiesGrouped.forEach((singleAct) => {
      if (singleAct.userForPipe && singleAct.userForPipe.length > 0) {
        singleAct.userForPipe.forEach((singleUser) => {
          userIds.forEach((otherUser) => {
            if (singleUser === otherUser) {
              ghostFilterd.push(singleAct);
            }
          });
        });
      }
    });
    ghostFilterd = filter(ghostFilterd, (item) => {
      if (startDate && startDate != 0 && endDate && endDate != 0) {
        dateMatch =
          moment(item.activityDate).startOf('day').valueOf() >=
            moment(startDate).startOf('day').valueOf() &&
          moment(item.activityDate).startOf('day').valueOf() <=
            moment(endDate).endOf('day').valueOf();
      }
      return dateMatch;
    });

    const uniqGhostFiltered = uniq(ghostFilterd);

    return uniqGhostFiltered;
  }

  checkIfContainerIdExistInFieldActivity(containerId: number): Array<FieldActivity> {
    const tmpActivity: FieldActivity[] = [];
    this.fieldActivityList
      .pipe(first((list: FieldActivity[]) => list && list.length > 0))
      .subscribe((activities: FieldActivity[]) => {
        activities.forEach((activity) => {
          if (activity?.mixes?.length > 0) {
            const hasContainer = activity.mixes.some((mix) => mix?.container?.id == containerId);
            if (hasContainer && !tmpActivity.includes(activity)) {
              tmpActivity.push(activity);
            }
          }
        });
      });
    return tmpActivity;
  }

  checkIfProductIdExistInFieldActivity(productId): Array<FieldActivity> {
    const tmpActivity: FieldActivity[] = [];
    this.fieldActivityList
      .pipe(first((list: FieldActivity[]) => list && list.length > 0))
      .subscribe((activities: FieldActivity[]) => {
        activities.forEach((activity) => {
          if (activity?.mixes?.length > 0) {
            const hasProduct = activity.mixes.some((mix) => mix?.product?.id == productId);
            if (hasProduct && !tmpActivity.includes(activity)) {
              tmpActivity.push(activity);
            }
          }
        });
      });
    return tmpActivity;
  }

  getActivityForClone(fieldActivity): FieldActivity {
    let fAToBeCloned: FieldActivity;
    delete fieldActivity.machineActivities;
    delete fieldActivity.machineActivity;
    delete fieldActivity.isAutomatched;
    delete fieldActivity.automaticActions;
    delete fieldActivity.id;
    delete fieldActivity.uuid;
    if (fieldActivity.mixes && fieldActivity.mixes.length > 0) {
      fieldActivity.mixes.forEach((mix) => {
        delete mix.fieldActivity;
        delete mix.uuid;
      });
    }
    if (fieldActivity.fieldActivityItems && fieldActivity.fieldActivityItems.length > 0) {
      fieldActivity.fieldActivityItems.forEach((item) => {
        delete item.fieldActivity;
        delete item.id;
      });
    }
    fAToBeCloned = fieldActivity;
    return fAToBeCloned;
  }

  getFielActivityGroupedForClone(FAGrouped: FieldActivityGrouped) {
    let FAGroupedToBeCloned: FieldActivityGrouped;
    delete FAGrouped.machineActivities;
    delete FAGrouped.isAutomatched;
    delete FAGrouped.automaticActions;
    delete FAGrouped.uuid;
    FAGrouped.equipmentActivities = [];
    FAGroupedToBeCloned = FAGrouped;
    return FAGroupedToBeCloned;
  }

  getFieldActivitiesCustomType() {
    return this.http
      .get<FieldActivitiesCustomType[]>(this.apiBaseUrl + 'fieldActivitiesCustomType')
      .pipe(
        map((response) => {
          const types = response.slice().sort((a, b) => a.customType.localeCompare(b.customType));

          this.fieldActivitiesCustomType$.next(types);
          return types;
        })
      );
  }

  saveFieldActivitiesCustomType(customType): Observable<any> {
    return this.http.post(this.apiBaseUrl + 'fieldActivitiesCustomType', customType).pipe(
      map((response) => {
        return response;
      })
    );
  }

  getFieldActivityGroupedBehaviorSubject(): Observable<Array<FieldActivityGrouped>> {
    return this.allFieldActivityGrouped$.asObservable();
  }

  setFieldActivityGroupedBehaviorSubject(list: Array<FieldActivityGrouped>) {
    this.allFieldActivityGrouped$.next(list);
  }

  getFieldActivityBehaviorSubject(): Observable<Array<FieldActivity>> {
    return this.allFieldActivity$.asObservable();
  }

  setFieldActivityBehaviorSubject(list: Array<FieldActivity>) {
    this.allFieldActivity$.next(list);
  }

  cleanNonUsefulDataByFAGrouped = (
    fieldActivityGroupedList: Array<FieldActivityGrouped>
  ): Array<FieldActivityGrouped> => {
    fieldActivityGroupedList.forEach((grouped) => {
      grouped.activities.forEach((activity) => {
        if (activity && activity.mixes && activity.mixes.length > 0) {
          activity.mixes.forEach((mix) => {
            if (mix && mix.fieldActivity && mix.fieldActivity.id) {
              mix.fieldActivity = { id: mix.fieldActivity.id } as FieldActivity;
            }
          });
        }
        if (activity?.fieldActivityItems?.length) {
          activity.fieldActivityItems.forEach((fieldActivityItem) => {
            if (fieldActivityItem?.fieldActivity?.id) {
              return {
                ...fieldActivityItem,
                fieldActivity: { id: fieldActivityItem.fieldActivity.id } as FieldActivity,
              };
            } else {
              return fieldActivityItem;
            }
          });
        }
        if (activity && activity.machineActivity) {
          delete activity.machineActivity;
        }
      });
    });
    // this.download(JSON.stringify(fag), 'activityMachineActivity.json', 'application/json');
    return fieldActivityGroupedList;
  };

  deleteFieldActivityCustomType(fieldActivityCustomType: any): Observable<any> {
    return this.http
      .delete(this.apiBaseUrl + 'fieldActivitiesCustomType/' + fieldActivityCustomType.id)
      .pipe(
        map((response) => {
          return response;
        })
      );
  }

  deleteFieldActivityItem(id: number): Observable<FieldActivityItem> {
    return this.http
      .delete(`${this.apiBaseUrl}fieldActivityItems/${id}`)
      .pipe(map(() => ({ id }) as FieldActivityItem));
  }

  //////////// PAGINATION///////////////

  getFieldActivitiesPaged(
    pageNumber: number,
    pageSize: number,
    year: number,
    donefilter: boolean | null,
    filterToApply = [],
    orderBy = null,
    textSearch = null
  ): Observable<PaginatedResponse<FieldActivity>> {
    const companySelected = JSON.parse(localStorage.getItem('companySelected'));
    const companyId = companySelected ? companySelected.id : null;
    if (donefilter != null) {
      filterToApply.push({
        name: 'done',
        value: donefilter,
      });
    }
    const filter = [
      ...filterToApply,
      {
        name: 'companyId',
        value: companyId,
      },
      {
        name: 'year',
        value: year,
      },
    ];
    const order =
      orderBy != null
        ? orderBy
        : [
            {
              property: 'activityDate',
              direction: 'DESC',
            },
          ];
    const filters = {
      number: pageNumber,
      max: pageSize,
      textSearch,
      sort: {
        orderBy: order,
      },
      filter: {
        filters: filter,
      },
    };

    return this.http
      .post<
        PaginatedResponse<FieldActivity>
      >(this.apiUrl + 'api/private/v2/fieldActivity?excludes=machineActivities', filters)
      .pipe(
        map((response) => {
          return response;
        })
      );
  }

  getFieldActivitiesGroupedPaged(
    pageNumber: number,
    pageSize: number,
    year: number,
    donefilter: boolean | null,
    filterToApply = [],
    orderBy = null,
    textSearch = null
  ): Observable<PaginatedResponse<FieldActivityGrouped>> {
    const companySelected = JSON.parse(localStorage.getItem('companySelected'));
    const companyId = companySelected ? companySelected.id : null;
    if (donefilter != null) {
      filterToApply.push({
        name: 'done',
        value: donefilter,
      });
    }
    const filter = [
      ...filterToApply,
      {
        name: 'companyId',
        value: companyId,
      },
      {
        name: 'year',
        value: year,
      },
    ];
    const order =
      orderBy != null
        ? orderBy
        : [
            {
              property: 'activityDate',
              direction: 'DESC',
            },
          ];

    const filters = {
      number: pageNumber,
      max: pageSize,
      textSearch,
      sort: {
        orderBy: order,
      },
      filter: {
        filters: filter,
      },
    };

    return this.http
      .post<
        PaginatedResponse<FieldActivityGrouped>
      >(this.apiUrl + 'api/private/v2/fieldActivityGroup?', filters)
      .pipe(
        map((response) => {
          return response;
        })
      );
  }

  getMostRecentActivityTypes(): Observable<string[]> {
    return this.http.get<string[]>(this.apiBaseUrl + 'activities/field/mostRecentTypes').pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Error fetching most recent activity types:', error);
        return of([]);
      })
    );
  }
}
